import i18n from 'lib/utility/i18n';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { Modal } from 'react-bootstrap';
import { Button } from '@everfi/reactifi/src/components/Button';
import { ApiUtil, FloatingLabelFormField, FAIcon } from 'reactifi';
import MediaChooser from './MediaChooser';
import MediaPreview from './MediaPreview';
import { IconCategoryValue } from '@everfi/reactifi/src/components/icon-category-value';

export const keyTab = {
  MEDIA: 1,
  UPLOAD: 2,
};

const getPreview = (preview, activeMediaUrl, showFileName) => (
  <div className="col-md-10 media-chooser-preview">
    {preview ? <MediaPreview showFileName={showFileName} url={activeMediaUrl} /> : null}
  </div>
);

const getDeleteButton = (dataAction, htmlId, canRemove, onRemove) => {
  if (!canRemove) return null;

  return (
    <div
      className="col-md-2"
      data-action={`${dataAction}-remove`}
      id={htmlId ? `${htmlId}_remove` : null}
      onClick={onRemove}
    >
      <FAIcon icon="trash" />
    </div>
  );
};

const showRequired = required => (required ? '*' : '');

const getHelpText = helpText => {
  if (!helpText) return null;

  return <span className="form-text p-l-5">{helpText}</span>;
};

const getInput = (props, handleChange, activeMediaUrl) => {
  const {
    buttonStyle,
    dataAction,
    dataObjectId,
    dataObjectType,
    htmlId,
    label: inputLabel,
    name,
    placeholder,
    required,
    type,
  } = props;

  const label = `${inputLabel || i18n.t('Select media')} ${showRequired(required)}`;

  return type === 'button' ? (
    <>
      <Button
        id={htmlId}
        style={buttonStyle}
        onClick={handleChange}
        data-action={dataAction}
        data-object-id={dataObjectId}
        data-object-type={dataObjectType}
        label={label}
        chakra="off"
      />
      <input type="hidden" name={name} value={activeMediaUrl} />
    </>
  ) : (
    <Fragment>
      <p>
        <strong>{label}</strong>
      </p>
      <FloatingLabelFormField
        label={label}
        placeholder={placeholder || label}
        caption={placeholder}
        type="text"
        name={name}
        onClick={handleChange}
        dataValue={activeMediaUrl}
        valueOverride={true}
      />
    </Fragment>
  );
};

export default class MediaChooserField extends React.Component {
  static defaultProps = {
    buttonStyle: 'primary',
    canRemove: false,
    preview: true,
    required: false,
    type: 'button',
    fileTypesText: 'JPG, PNG, or GIF',
  };

  static propTypes = {
    beforeOpenCallback: PropTypes.func,
    afterCloseCallback: PropTypes.func,
    accountId: PropTypes.number,
    activeMediaUrl: PropTypes.string,
    buttonStyle: PropTypes.string,
    canRemove: PropTypes.bool,
    className: PropTypes.string,
    dataAction: PropTypes.string,
    dataObjectId: PropTypes.string,
    dataObjectType: PropTypes.string,
    helpText: PropTypes.string,
    htmlId: PropTypes.string,
    label: PropTypes.string,
    onMediaSelected: PropTypes.func,
    name: PropTypes.string.isRequired,
    newStyle: PropTypes.bool,
    placeholder: PropTypes.string,
    preview: PropTypes.bool,
    required: PropTypes.bool,
    showFileName: PropTypes.bool,
    type: PropTypes.string,
    fileTypesText: PropTypes.string,
    contentType: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      activeMediaUrl: props.activeMediaUrl,
      activeMedia: null,
      activeTab: keyTab.MEDIA,
      canRemove: props.canRemove,
      showModal: false,
      showUnsavedModal: false,
      media_name: '',
      media_description: '',
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.activeMediaUrl !== this.props.activeMediaUrl) {
      this.setState({ activeMediaUrl: this.props.activeMediaUrl });
    }
  }

  handleChange = event => {
    event.preventDefault();
    this.setState({ showModal: true });

    if (this.props.beforeOpenCallback) {
      this.props.beforeOpenCallback();
    }
  };

  onHandleFormChange = event => this.setState({ [event.target.id]: event.target.value });

  setActiveTab = tab => this.setState({ activeTab: Number(tab) });

  handleChangeActiveMedia = activeMedia => this.setState({ activeMedia });

  closeModal = () => {
    this.setState({ showUnsavedModal: false, showModal: false, activeMedia: null });

    if (this.props.afterCloseCallback) {
      this.props.afterCloseCallback();
    }
  };

  onCancelModal = () => (this.state.activeMedia ? this.setState({ showUnsavedModal: true }) : this.closeModal());

  onCloseUnsavedModal = () => this.setState({ showUnsavedModal: false });

  mediaSelected = () => {
    const { activeMedia, activeTab, media_name, media_description } = this.state;
    const media = {
      name: media_name,
      description: media_description,
      url: activeMedia.url,
    };
    const data = { type: 'next_media', attributes: media };

    if (this.props.accountId) {
      media.account_id = this.props.accountId;
    }

    if (activeTab === keyTab.UPLOAD) {
      ApiUtil.requestApiData(
        `/api/data/next_media`,
        false,
        'POST',
        {
          body: JSON.stringify({ data }),
        },
        false
      );
    }

    if (this.props?.onMediaSelected) {
      this.props.onMediaSelected(media);
    }

    this.setState({
      activeMedia: null,
      activeMediaUrl: activeMedia.url,
      canRemove: true,
      showModal: false,
    });

    if (this.props.afterCloseCallback) {
      this.props.afterCloseCallback();
    }
  };

  onRemove = () => {
    this.setState({
      activeMediaUrl: '',
      canRemove: false,
    });
  };

  imgPreview = (preview, activeMediaUrl, dataAction, htmlId, canRemove, showFileName) => {
    return (
      <>
        {getPreview(preview, activeMediaUrl, showFileName)}
        {getDeleteButton(dataAction, htmlId, canRemove, this.onRemove)}
      </>
    );
  };

  confirmModal = () => (
    <Modal
      className="modal-size-small media-confirm-modal"
      backdropClassName="media-confirm-modal"
      show={this.state.showUnsavedModal}
    >
      <Modal.Header>
        <Modal.Title>{i18n.t('Unsaved changes')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <span>{i18n.t('Are you sure you want to leave this page?')}</span>
      </Modal.Body>
      <Modal.Footer>
        <Button className="pull-left" style="secondary" onClick={this.onCloseUnsavedModal}>
          {i18n.t('Cancel')}
        </Button>
        <Button className="pull-right" bsStyle="primary" onClick={this.closeModal}>
          {i18n.t('Yes')}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  render() {
    const { activeMedia, activeMediaUrl, canRemove, showModal } = this.state;
    const {
      buttonStyle,
      className,
      dataAction,
      dataObjectId,
      dataObjectType,
      helpText,
      htmlId,
      label,
      name,
      newStyle,
      preview,
      required,
      showFileName,
      fileTypesText,
    } = this.props;
    const wrapperClassName = ['fg-float', className].filter(Boolean).join(' ');

    return (
      <>
        {newStyle ? (
          <div className={wrapperClassName}>
            <h3 className="card-title card-title-18 m-t-0">{label}</h3>
            {helpText && <span className="input-hint">{helpText}</span>}
            <div className="m-t-20">
              <IconCategoryValue category={i18n.t('Image Format')} value={i18n.t(fileTypesText)} />
            </div>
            <div className="media-chooser-parity-style">
              {!!activeMediaUrl &&
                this.imgPreview(preview, activeMediaUrl, dataAction, htmlId, canRemove, showFileName)}
            </div>
            <br className="clearfix" />
            <Button
              id={htmlId}
              style={buttonStyle}
              onClick={this.handleChange}
              data-action={dataAction}
              data-object-id={dataObjectId}
              data-object-type={dataObjectType}
              label={i18n.t(`Upload Image ${showRequired(required)}`)}
            />
            <input type="hidden" name={name} value={activeMediaUrl} />
          </div>
        ) : (
          <div className={wrapperClassName}>
            {getInput(this.props, this.handleChange, activeMediaUrl)}
            {getHelpText(helpText)}
            <div className="row">
              {!!activeMediaUrl && this.imgPreview(preview, activeMediaUrl, dataAction, htmlId, canRemove)}
            </div>
            <br className="clearfix" />
          </div>
        )}
        <Modal animation={false} className={`modal-size-large ${name}`} onHide={this.onCancelModal} show={showModal}>
          <Modal.Header closeButton={true}>
            <Modal.Title>{i18n.t('Media Chooser')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <MediaChooser
              {...this.props}
              onActiveMediaChange={activeMedia => this.handleChangeActiveMedia(activeMedia)}
              onDblClickMedia={this.mediaSelected}
              setActiveTab={this.setActiveTab}
              activeTab={this.state.activeTab}
              onHandleFormChange={this.onHandleFormChange}
            />
          </Modal.Body>
          <Modal.Footer className="media-footer">
            <Button data-action="cancel" style="secondary" branded="true" type="cancel" onClick={this.onCancelModal}>
              {i18n.t('Cancel')}
            </Button>
            <Button
              style="primary"
              branded="true"
              data-action="select-media"
              data-object-id={'activeMedia.id'}
              data-object-type="media"
              onClick={this.mediaSelected}
              disabled={!activeMedia}
            >
              {i18n.t('Select')}
            </Button>
          </Modal.Footer>
        </Modal>
        {this.confirmModal()}
      </>
    );
  }
}
